import type { RefObject } from 'react';
import { useEffect } from 'react';

interface UseAddAttributesOptions {
  selector?: string;
  ref?: RefObject<HTMLElement | null>;
  attributes: Record<string, string>;
  condition?: boolean;
  delay?: number;
}

export const useAddAttributes = ({
  selector,
  ref,
  attributes,
  condition = true,
  delay = 100,
}: UseAddAttributesOptions) => {
  useEffect(() => {
    if (!condition) {
      return () => null;
    }

    // timeout is used to wait for the element to be rendered in the DOM
    // before trying to add attributes to it
    const timeout = setTimeout(() => {
      let element: HTMLElement | null = null;

      // Try to get element from ref first, then from selector
      if (ref?.current) {
        element = ref.current;
      } else if (selector) {
        element = document.querySelector(selector);
      }

      if (element) {
        Object.entries(attributes).forEach(([key, value]) => {
          element!.setAttribute(key, value);
        });
      }
    }, delay);

    return () => clearTimeout(timeout);
  }, [condition, selector, ref, attributes, delay]);
};
